import { useEffect, useMemo, useRef, useState } from 'react';
import { Type } from '@sinclair/typebox';
import { Container, Stack, Group, Text, ScrollArea, Checkbox } from '@mantine/core';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import partnerSchemaDefinition from '@ayocore/erp/crm/schemas/partner.schema';
import type { Partner } from '@ayocore/erp/crm/schemas/partner.schema';
import type { PartnerRole } from '@ayocore/erp/crm/schemas/partner-role.schema';
import { StringField, IntegerField } from '@ayocore/platform/common/helpers/schema-types';
import {
  usePartnerRepository,
  useSalesAreaRepository,
  usePartnerRoleRepository,
  useLeadSourceRepository,
  useSegmentRepository,
} from '@ayocore/erp/crm/client/rpc';
import { userRepository } from '@ayocore/platform/identity/client/rpc';
import { CRM_PARTNER_ROLES } from '@ayocore/erp/crm/constants';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';
import { useNavigate } from 'react-router';
import { CompanyAutocomplete } from '@ayocore/erp/crm/client/components/Partners/forms/CompanyAutocomplete';
import { CollapsibleSection } from './CollapsibleSection';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { ExternalCompanySearchView } from '@ayocore/erp/crm/client/components/ExternalCompanySearchView';
import { companyInfoToFormDto } from '@ayocore/erp/crm/common/types/company';
import type { CompanyInfo } from '@ayocore/erp/crm/common/types/company';

/**
 * Расширяем createSchema дополнительными полями для контактных каналов.
 * Схема совпадает с типом PartnerRepository.createExtended() — деструктуризация при сабмите не нужна.
 * Определено на уровне модуля, чтобы объект схемы не пересоздавался при каждом рендере.
 */
const grasiasCreateSchema = Type.Object({
  ...partnerSchemaDefinition.createSchema.properties,
  email: StringField({ title: 'Email', nullable: true, maxLength: 255 }),
  phone: StringField({ title: 'Телефон', nullable: true, maxLength: 50 }),
  website: StringField({
    title: 'Сайт',
    nullable: false,
    minLength: 1,
    maxLength: 500,
    error: 'Укажите сайт компании',
  }),
  /**
   * Сфера деятельности — одна запись справочника сегментов. У партнёра это
   * связь «многие ко многим», но на практике сегмент почти всегда один, поэтому
   * при заведении выбираем одну; на карточке остаётся мультивыбор.
   * В createExtended уходит массивом segment_ids.
   */
  segment_id: IntegerField({ title: 'Сфера деятельности', nullable: true }),
});

/**
 * CreatePartnerFormGrasias — форма создания партнёра для клиента Грасиас.
 *
 * Отличия от базовой CreatePartnerForm:
 * - Обязательные поля: регион (sales_area_id), менеджер (manager_id), сайт, телефон ИЛИ email
 * - Регион вынесен в основную секцию (не скрыт)
 * - Подсказка о дублях: при вводе названия ищем похожих партнёров в системе
 * - Выбор ролей партнёра (чекбоксы)
 *
 * Регистрируется через CrmExtensions в grasias/client/bootstrap.ts.
 */
export function CreatePartnerFormGrasias({
  onPartnerCreated,
  createdFromLeadId,
  defaultSourceId,
}: {
  onPartnerCreated?: (partnerId?: number) => void;
  /** Лид, из карточки которого открыли форму — пометка происхождения партнёра. */
  createdFromLeadId?: number | null;
  /** Источник того же лида — подставляем, но оставляем редактируемым. */
  defaultSourceId?: number | null;
}) {
  const partnerRepository = usePartnerRepository();
  const salesAreaRepository = useSalesAreaRepository();
  const partnerRoleRepository = usePartnerRoleRepository();
  const leadSourceRepository = useLeadSourceRepository();
  const segmentRepository = useSegmentRepository();
  const currentUser = useAuthController(s => s.user);
  const navigate = useNavigate();
  const searchModal = useModal(ExternalCompanySearchView);

  type CreatePartnerExtended = Parameters<typeof partnerRepository.createExtended>[0];

  // Активные не-системные пользователи, отсортированные по русскому алфавиту
  const managerRepo = useMemo(() => userRepository.onlyHumanActive().orderByName('asc'), []);

  /**
   * Происхождение и источник приходят из карточки лида; created_from_lead_id
   * невидимое поле — попадает в values только отсюда.
   *
   * Тип указан явно: useSchemaForm выводит тип значений формы из options,
   * и литерал из двух полей сузил бы его, сломав типизацию всех остальных.
   */
  const initialValues: Record<string, unknown> = {
    source_id: defaultSourceId ?? null,
    created_from_lead_id: createdFromLeadId ?? null,
  };

  const SchemaForm = useSchemaForm(grasiasCreateSchema, {
    hideDescriptions: true,
    initialValues,
    customValidation: values => {
      const errors: Record<string, string> = {};

      if (!String(values.phone ?? '').trim() && !String(values.email ?? '').trim()) {
        errors.phone = 'Укажите телефон или email';
        errors.email = 'Укажите телефон или email';
      }

      if (!(values as Partial<Partner>).sales_area_id) {
        errors.sales_area_id = 'Обязательное поле';
      }

      // Менеджер обязателен только для роли "Клиент"
      const isCustomer =
        customerRoleIdRef.current !== null &&
        selectedRoleIdsRef.current.includes(customerRoleIdRef.current);
      if (isCustomer && !(values as Partial<Partner>).manager_id) {
        errors.manager_id = 'Обязательное поле';
      }

      // Источник и сфера деятельности обязательны для клиента — те же правила
      // дублирует сервер (grasias/server/rules/partner_create_rules.ts)
      if (isCustomer && !(values as Partial<Partner>).source_id) {
        errors.source_id = 'Обязательное поле';
      }

      if (isCustomer && !values.segment_id) {
        errors.segment_id = 'Обязательное поле';
      }

      return errors;
    },
  });

  const [showNotes, setShowNotes] = useState(false);
  const [showRequisites, setShowRequisites] = useState(false);

  // Роли партнёра
  const [availableRoles, setAvailableRoles] = useState<PartnerRole[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [rolesError, setRolesError] = useState<string | null>(null);

  // ID роли "Клиент" — определяется при загрузке справочника ролей
  const [customerRoleId, setCustomerRoleId] = useState<number | null>(null);
  // Рефы для корректной работы customValidation (стабильная ссылка, не stale-замыкание)
  const customerRoleIdRef = useRef<number | null>(null);
  const selectedRoleIdsRef = useRef<number[]>([]);

  /**
   * Выбрана ли роль «Клиент» — от неё зависит обязательность менеджера
   * и источника. Внутри customValidation тот же признак считается по рефам:
   * там нужна стабильная ссылка без stale-замыкания.
   */
  const isCustomerSelected = customerRoleId !== null && selectedRoleIds.includes(customerRoleId);
  // Предзаполнение при загрузке текущего пользователя (аналог setInitialData в edit-формах)
  useEffect(() => {
    if (currentUser?.id) {
      SchemaForm.setInitialData({ manager_id: currentUser.id });
    }
  }, [currentUser?.id]);

  // Загрузка активных ролей при монтировании; по умолчанию выбираем роль "Клиент"
  useEffect(() => {
    const load = async () => {
      try {
        const roles = await partnerRoleRepository
          .where('is_active', true)
          .orderBy('name', 'asc')
          .fetch();
        setAvailableRoles(roles as PartnerRole[]);

        const customerRole = (roles as PartnerRole[]).find(
          r => r.code === CRM_PARTNER_ROLES.CUSTOMER
        );
        if (customerRole) {
          customerRoleIdRef.current = customerRole.id;
          setCustomerRoleId(customerRole.id);
          selectedRoleIdsRef.current = [customerRole.id];
          setSelectedRoleIds([customerRole.id]);
        }
      } catch {
        // справочник не критичен
      }
    };
    load();
  }, []);

  /** Выбор компании из внешнего поиска (ЕГРЮЛ/DaData) — автозаполнение полей формы */
  const handleCompanySelect = (company: CompanyInfo) => {
    const dto = companyInfoToFormDto(company);
    SchemaForm.setFieldValue('name', dto.short_name);
    SchemaForm.setFieldValue('full_name', dto.full_name ?? '');
    SchemaForm.setFieldValue('inn', dto.inn);
    SchemaForm.setFieldValue('ogrn', dto.ogrn ?? '');
    SchemaForm.setFieldValue('address', dto.legal_address ?? '');
    SchemaForm.setFieldValue('director', company.director ?? '');
    searchModal.close();
  };

  const openSearch = () => {
    searchModal.open(
      {
        title: 'Найти компанию',
        initialQuery: String(SchemaForm.values.name ?? '').trim(),
        onSelect: handleCompanySelect,
      },
      { size: 'lg', background: 'glassmorphism' }
    );
  };

  const handleSubmit = async (values: typeof SchemaForm.values) => {
    if (selectedRoleIds.length === 0) {
      setRolesError('Выберите хотя бы один тип партнёра');
      throw new Error('Выберите хотя бы один тип партнёра');
    }
    setRolesError(null);

    // segment_id — поле формы, а не колонка партнёра: вынимаем его из values,
    // иначе неизвестная колонка ушла бы в create(). В createExtended сегменты
    // передаются массивом.
    const { segment_id: segmentId, ...partnerValues } = values;

    const newPartner = await partnerRepository.createExtended({
      ...(partnerValues as CreatePartnerExtended),
      role_ids: selectedRoleIds,
      segment_ids: segmentId ? [Number(segmentId)] : [],
    });

    SchemaForm.setSuccess(`Партнер "${newPartner.name}" успешно создан! ID: ${newPartner.id}`);
    SchemaForm.reset();
    // Восстанавливаем дефолтный выбор роли "Клиент"
    const defaultIds = customerRoleIdRef.current !== null ? [customerRoleIdRef.current] : [];
    setSelectedRoleIds(defaultIds);
    selectedRoleIdsRef.current = defaultIds;
    setRolesError(null);

    setTimeout(() => {
      if (onPartnerCreated) {
        onPartnerCreated(newPartner.id);
      }
    }, 1500);

    return newPartner;
  };

  const handleCancel = () => {
    SchemaForm.reset();
    SchemaForm.clearMessages();
    // Восстанавливаем дефолтный выбор роли "Клиент"
    const defaultIds = customerRoleIdRef.current !== null ? [customerRoleIdRef.current] : [];
    setSelectedRoleIds(defaultIds);
    selectedRoleIdsRef.current = defaultIds;
    setRolesError(null);
    if (onPartnerCreated) {
      onPartnerCreated();
    }
  };

  const handleOpenPartner = (partnerId: number) => {
    navigate(`/crm/partners/${partnerId}`);
    onPartnerCreated?.();
  };

  return (
    <form
      onSubmit={SchemaForm.handleSubmit(handleSubmit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <ScrollArea.Autosize flex={1} mih={0} scrollbarSize={0}>
        <Container size='lg' px={{ base: 'xs', sm: 'sm' }} py='xs' maw='100%'>
          <Stack gap='xs'>
            <SchemaForm.Messages />

            {/* Основная информация */}
            <Stack gap='xs'>
              <CompanyAutocomplete
                value={String(SchemaForm.values.name ?? '')}
                onChange={value => SchemaForm.setFieldValue('name', value)}
                onPartnerSelect={partner => handleOpenPartner(partner.id)}
                onOpenSearch={openSearch}
                label='Короткое название'
                placeholder='Пример: Grasias gift'
                size='xs'
                error={
                  SchemaForm.showValidationErrors && SchemaForm.errors.name
                    ? String(SchemaForm.errors.name)
                    : undefined
                }
              />

              <SchemaForm.Field.full_name
                size='xs'
                label='Полное наименование'
                placeholder='Пример: Имидж Трейдинг/Экспресс Сувенир/ИП Романова'
              />

              <Stack gap={4}>
                <Text
                  size='xs'
                  c='blue'
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setShowNotes(prev => !prev)}
                >
                  {showNotes ? 'Скрыть примечание к партнёру' : 'Указать примечание к партнёру'}
                </Text>
                {showNotes && <SchemaForm.Field.notes size='xs' />}
              </Stack>

              {/* Регион + источник (источник обязателен для роли «Клиент») */}
              <Group grow>
                <SchemaForm.Field.sales_area_id
                  size='xs'
                  label='Регион *'
                  repo={salesAreaRepository.filterActive().orderBy('name', 'asc').limit(200)}
                  displayField='name'
                  placeholder='Выберите регион...'
                />
                <SchemaForm.Field.source_id
                  size='xs'
                  label={`Источник${isCustomerSelected ? ' *' : ''}`}
                  repo={leadSourceRepository.filterActive().orderBy('sort_order', 'asc')}
                  displayField='name'
                  placeholder='Откуда пришёл...'
                />
              </Group>

              {/* Сфера деятельности — обязательна для роли «Клиент» */}
              <SchemaForm.Field.segment_id
                size='xs'
                label={`Сфера деятельности${isCustomerSelected ? ' *' : ''}`}
                repo={segmentRepository.where('is_active', true).orderBy('name', 'asc').limit(500)}
                displayField='name'
                placeholder='Чем занимается...'
              />

              {/* Менеджер — обязателен только если выбрана роль "Клиент" */}
              <SchemaForm.Field.manager_id
                size='xs'
                label={`Менеджер${isCustomerSelected ? ' *' : ''}`}
                repo={managerRepo}
                displayField='name'
                placeholder='Выберите менеджера...'
                disabled={!isCustomerSelected}
                initialOption={
                  currentUser?.id && currentUser.name
                    ? { value: String(currentUser.id), label: currentUser.name }
                    : undefined
                }
              />

              {/* Email + Телефон */}
              <Group grow>
                <SchemaForm.Field.email size='xs' label='Email *' placeholder='work@example.com' />
                <SchemaForm.Field.phone
                  size='xs'
                  label='Телефон *'
                  placeholder='+7 999 123-45-67'
                />
              </Group>

              {/* Сайт */}
              <SchemaForm.Field.website
                size='xs'
                label='Сайт *'
                placeholder='https://example.com'
              />

              {/* Роли партнёра */}
              {availableRoles.length > 0 && (
                <Stack gap={4}>
                  <Text size='xs' fw={500} c='dimmed'>
                    Тип партнёра *
                  </Text>
                  <Group gap='md'>
                    {availableRoles.map(role => (
                      <Checkbox
                        key={role.id}
                        size='xs'
                        label={role.name}
                        checked={selectedRoleIds.includes(role.id)}
                        onChange={e => {
                          const checked = e.currentTarget.checked;
                          setRolesError(null);
                          setSelectedRoleIds(prev => {
                            const next = checked
                              ? [...prev, role.id]
                              : prev.filter(id => id !== role.id);
                            selectedRoleIdsRef.current = next;
                            return next;
                          });
                          // Снимаем роль "Клиент" — менеджер больше не нужен, поле очищаем
                          if (!checked && role.id === customerRoleIdRef.current) {
                            SchemaForm.setFieldValue('manager_id', null);
                          }
                        }}
                      />
                    ))}
                  </Group>
                  {rolesError && (
                    <Text size='xs' c='red'>
                      {rolesError}
                    </Text>
                  )}
                </Stack>
              )}
            </Stack>

            {/* Реквизиты — свёрнуты: при заведении их обычно подтягивает поиск по ЕГРЮЛ */}
            <CollapsibleSection
              label='Реквизиты'
              open={showRequisites}
              onToggle={() => setShowRequisites(prev => !prev)}
            >
              <SchemaForm.Field.address size='xs' />
              <Group grow>
                <SchemaForm.Field.inn size='xs' />
                <SchemaForm.Field.ogrn size='xs' />
              </Group>
            </CollapsibleSection>

            {/* Директор заполняется поиском по ЕГРЮЛ, поле не показываем */}
            <input type='hidden' name='director' value={String(SchemaForm.values.director ?? '')} />
          </Stack>
        </Container>
      </ScrollArea.Autosize>

      {/* Кнопки */}
      <div
        style={{
          borderTop: '1px solid #e9ecef',
          backgroundColor: 'white',
          padding: '8px 0 0',
          flexShrink: 0,
        }}
      >
        <Group justify='flex-end'>
          <SchemaForm.buttons.cancel onClick={handleCancel} label='Отмена' size='xs' />
          <SchemaForm.buttons.submit label='Создать партнера' size='xs' />
        </Group>
      </div>
    </form>
  );
}

export default CreatePartnerFormGrasias;
